import { useEffect, useState, useCallback } from 'react';
import { useLocation, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from 'components/AuthStore';
import {
  getPrimaryRole,
  getNavConfig,
  isPathAllowed,
  ROLES
} from 'config/navigation';
import SidebarItem from './components/SidebarItem/SidebarItem';
import SidebarGroup, {
  hasActiveChild
} from './components/SidebarGroup/SidebarGroup';

const SIDEBAR_STORAGE_KEY = 'sidebar-collapsed';

const saveSidebarState = (collapsed) => {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? '1' : '0');
  } catch (_) {}
};

const setupMobileToggle = () => {
  const handleToggle = (e) => {
    e.preventDefault();
    const isCurrentlyActive = document.body.classList.contains('menu-active');
    document.body.classList.toggle('menu-active');
    // Only persist on desktop (on mobile menu-active means "open", not "collapsed")
    if (window.innerWidth > 1024) {
      saveSidebarState(!isCurrentlyActive);
    }
    if (!isCurrentlyActive) {
      window.dispatchEvent(new CustomEvent('sidebar:collapse'));
    }
  };
  const handleOverlayClick = () => {
    document.body.classList.remove('menu-active');
    window.dispatchEvent(new CustomEvent('sidebar:collapse'));
  };

  const toggleBtns = document.querySelectorAll('.menu-toggle');
  const overlay = document.querySelector('.bg-overlay');

  toggleBtns.forEach((btn) => btn.addEventListener('click', handleToggle));
  overlay?.addEventListener('click', handleOverlayClick);

  return () => {
    toggleBtns.forEach((btn) => btn.removeEventListener('click', handleToggle));
    overlay?.removeEventListener('click', handleOverlayClick);
  };
};

const SidebarBrand = ({ onToggle }) => (
  <div className="navbar-brand-box">
    <div className="logo">
      <img
        src={
          process.env.REACT_APP_STATIC_URL +
          'content/assets/images/logo_hst_chu_den.png'
        }
        alt="logo"
        style={{ height: '72px' }}
        className="logo-full"
      />
      <img
        src={
          process.env.REACT_APP_STATIC_URL +
          'content/assets/images/icon-user.png'
        }
        alt=""
        style={{ height: '35px' }}
        className="logo-mini"
      />
    </div>
    <div
      className="nav-link menu-toggle"
      style={{ cursor: 'pointer' }}
      onClick={onToggle}>
      <span className="icon-menu" />
    </div>
  </div>
);

const findActiveGroupId = (menuItems, pathname) => {
  const activeGroup = menuItems.find(
    (item) => item.children && hasActiveChild(pathname, item.children)
  );
  return activeGroup ? activeGroup.id : null;
};

const Sidebar = () => {
  const user = useSelector(selectUser);
  const { pathname } = useLocation();

  const primaryRole = getPrimaryRole(user?.roles);
  const navConfig = primaryRole ? getNavConfig(primaryRole) : null;

  const visibleItems = navConfig
    ? navConfig.menu.filter((item) => {
        if (item.hidden) return false;
        if (item.requireSchool && !user?.roles?.school) return false;
        if (item.condition && !item.condition(user)) return false;
        return true;
      })
    : [];

  const [openGroupId, setOpenGroupId] = useState(() =>
    findActiveGroupId(visibleItems, pathname)
  );

  // Restore collapsed state on desktop
  useEffect(() => {
    if (window.innerWidth > 1024) {
      try {
        const collapsed = localStorage.getItem(SIDEBAR_STORAGE_KEY);
        if (collapsed === '1') {
          document.body.classList.add('menu-active');
        }
      } catch (_) {}
    }
  }, []);

  useEffect(() => {
    const cleanup = setupMobileToggle();
    return cleanup;
  }, []);

  useEffect(() => {
    const handleCollapse = () => setOpenGroupId(null);
    window.addEventListener('sidebar:collapse', handleCollapse);
    return () => window.removeEventListener('sidebar:collapse', handleCollapse);
  }, []);

  useEffect(() => {
    const examPaths = [
      '/thi',
      '/thi-thpt-2025',
      '/thi-thu',
      '/thi-thu-thpt-qg'
    ];
    if (examPaths.includes(pathname)) {
      document.body.classList.remove('menu-active');
    }
  }, [pathname]);

  useEffect(() => {
    const activeGroupId = findActiveGroupId(visibleItems, pathname);
    if (activeGroupId) {
      setOpenGroupId(activeGroupId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleSidebarToggle = (e) => {
    e.preventDefault();
    const isCurrentlyActive = document.body.classList.contains('menu-active');
    document.body.classList.toggle('menu-active');
    if (!isCurrentlyActive) {
      setOpenGroupId(null);
    }
  };

  const handleGroupToggle = useCallback((groupId) => {
    setOpenGroupId((prev) => (prev === groupId ? null : groupId));
  }, []);

  if (primaryRole === ROLES.ADMIN) {
    return <Redirect to="/not-support-role" />;
  }

  if (!primaryRole) {
    return null;
  }

  const skipPermissionCheck = [
    '/thi',
    '/thi-thpt-2025',
    '/not-found',
    '/not-support-role',
    '/sso-error',
    '/dang-nhap'
  ];
  if (
    !skipPermissionCheck.some(
      (p) => pathname === p || pathname.startsWith(p + '/')
    ) &&
    !isPathAllowed(primaryRole, pathname)
  ) {
    window.location.href = '/not-found';
    return null;
  }

  const menuId = `menu-${primaryRole}`;

  return (
    <div className="sidebar" id={menuId}>
      <SidebarBrand onToggle={handleSidebarToggle} />

      <div className="sidebar-menu-scroll">
        <ul className="sidebar-menu">
          {visibleItems.map((item) => {
            const processedItem = item.children
              ? {
                  ...item,
                  children: item.children.filter((child) => {
                    if (child.hidden) return false;
                    if (child.condition && !child.condition(user)) return false;
                    return true;
                  })
                }
              : item;

            if (processedItem.children && processedItem.children.length > 0) {
              return (
                <SidebarGroup
                  key={item.id}
                  item={processedItem}
                  isOpen={openGroupId === item.id}
                  onToggle={() => handleGroupToggle(item.id)}
                />
              );
            }
            return (
              <SidebarItem
                key={item.id}
                item={processedItem}
                disableActive={false}
              />
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
